import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Home, UserPlus, Copy, RefreshCw, LogOut, X, Crown, Loader2, Check, Send } from "lucide-react";
import { API, AuthContext } from "../App";

/**
 * Renders the user-facing Household / Family-Mode UI.
 * Used as a tab inside MyAccountPanel.
 * - If user is in a household → show members, invite tools (owner only), leave.
 * - Else if user has a pending request → show pending state.
 * - Else if user has pending invitations → show accept/decline.
 * - Else → show request form + invite-code join form.
 */
const HouseholdSection = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState({ household: null, pending_request: null, pending_invites: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [requestName, setRequestName] = useState("");
  const [requestReason, setRequestReason] = useState("");

  const [joinCode, setJoinCode] = useState("");
  const [inviteUsername, setInviteUsername] = useState("");

  const authHeader = { Authorization: `Bearer ${token}` };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/household/me`, { headers: authHeader });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load household:", err);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const handleErr = (err, fallback) => {
    const d = err?.response?.data?.detail;
    toast.error(Array.isArray(d) ? (d[0]?.msg || fallback) : (d || fallback));
  };

  const submitRequest = async () => {
    if (!requestName.trim()) { toast.error("Household name is required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/household/request`, { name: requestName.trim(), reason: requestReason.trim() }, { headers: authHeader });
      toast.success("Request submitted — an admin will review it soon.");
      setRequestName("");
      setRequestReason("");
      refresh();
    } catch (err) { handleErr(err, "Failed to submit request"); }
    finally { setBusy(false); }
  };

  const joinByCode = async () => {
    if (!joinCode.trim()) { toast.error("Enter an invite code"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/household/join`, { invite_code: joinCode.trim() }, { headers: authHeader });
      toast.success(r.data?.message || "Joined!");
      setJoinCode("");
      refresh();
    } catch (err) { handleErr(err, "Failed to join household"); }
    finally { setBusy(false); }
  };

  const acceptInvite = async (household_id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/household/accept-invite/${household_id}`, {}, { headers: authHeader });
      toast.success(r.data?.message || "Joined!");
      refresh();
    } catch (err) { handleErr(err, "Failed to accept invite"); }
    finally { setBusy(false); }
  };

  const declineInvite = async (household_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/household/decline-invite/${household_id}`, {}, { headers: authHeader });
      toast.success("Invitation declined.");
      refresh();
    } catch (err) { handleErr(err, "Failed to decline"); }
    finally { setBusy(false); }
  };

  const sendInvite = async () => {
    if (!inviteUsername.trim()) { toast.error("Enter a username"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/household/invite-by-username`, { username: inviteUsername.trim() }, { headers: authHeader });
      toast.success(r.data?.message || "Invitation sent");
      setInviteUsername("");
      refresh();
    } catch (err) { handleErr(err, "Failed to send invite"); }
    finally { setBusy(false); }
  };

  const regenCode = async () => {
    if (!window.confirm("Regenerate the invite code? The old code will stop working.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/household/regenerate-invite-code`, {}, { headers: authHeader });
      toast.success(`New code: ${r.data.invite_code}`);
      refresh();
    } catch (err) { handleErr(err, "Failed to regenerate code"); }
    finally { setBusy(false); }
  };

  const leaveHousehold = async () => {
    if (!window.confirm("Leave this household? You'll lose the 2-per-IP bypass and need an invite to rejoin.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/household/leave`, {}, { headers: authHeader });
      toast.success("Left the household.");
      refresh();
    } catch (err) { handleErr(err, "Failed to leave"); }
    finally { setBusy(false); }
  };

  const removeMember = async (member_user_id, username) => {
    if (!window.confirm(`Remove ${username} from the household?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/household/member/${member_user_id}`, { headers: authHeader });
      toast.success("Member removed.");
      refresh();
    } catch (err) { handleErr(err, "Failed to remove member"); }
    finally { setBusy(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success("Invite code copied!"),
      () => toast.error("Copy failed — select and copy manually.")
    );
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center text-xs text-gray-600" data-testid="household-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading household...
      </div>
    );
  }

  const h = data.household;

  return (
    <div className="space-y-4" data-testid="household-section">
      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#808080]">
        <Home className="w-5 h-5 text-[#7B0099]" />
        <h3 className="font-bold text-sm">Family / Household Mode</h3>
      </div>

      <div className="bg-[#FFF9D9] border border-[#E5C400] p-2 text-[11px] rounded">
        Households let trusted family members share one IP without the 2-account-per-IP limit.
        Up to <b>unlimited</b> members per household. Admin approval required.
      </div>

      {/* CASE 1: User is already in a household */}
      {h && (
        <div className="retro-window" data-testid="household-card">
          <div className="bg-gradient-to-r from-purple-700 to-pink-600 text-white px-3 py-1.5 font-bold text-sm flex items-center gap-2">
            <Home className="w-4 h-4" />
            <span className="truncate flex-1">{h.name}</span>
            {h.is_owner && <span className="bg-yellow-400 text-black text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1"><Crown className="w-3 h-3" /> OWNER</span>}
          </div>
          <div className="p-3 bg-[#E8E8E8] space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-600">Owner:</span> <b>{h.owner_username}</b></div>
              <div><span className="text-gray-600">Status:</span> <b className="text-green-700">{h.status}</b></div>
              <div><span className="text-gray-600">Members:</span> <b>{h.member_count}</b>{h.max_members ? ` / ${h.max_members}` : " (unlimited)"}</div>
              <div><span className="text-gray-600">Created:</span> <b>{h.created_at ? new Date(h.created_at).toLocaleDateString() : "—"}</b></div>
            </div>

            {h.is_owner && h.invite_code && (
              <div className="bg-white border-2 border-dashed border-purple-400 p-2 rounded">
                <div className="text-[10px] uppercase tracking-wider text-purple-700 mb-1">Invite Code (share with family)</div>
                <div className="flex items-center gap-2">
                  <code className="bg-purple-100 px-2 py-1 rounded text-sm font-bold tracking-widest flex-1" data-testid="household-invite-code">{h.invite_code}</code>
                  <button onClick={() => copyCode(h.invite_code)} className="retro-button px-2 py-1 text-xs flex items-center gap-1" data-testid="copy-invite-code-btn"><Copy className="w-3 h-3" /></button>
                  <button onClick={regenCode} disabled={busy} className="retro-button px-2 py-1 text-xs flex items-center gap-1" data-testid="regen-invite-code-btn"><RefreshCw className="w-3 h-3" /></button>
                </div>
                <div className="text-[10px] text-gray-600 mt-1">Family members can enter this code during signup to bypass the IP limit.</div>
              </div>
            )}

            {/* Members list */}
            <div>
              <div className="text-[11px] font-bold text-gray-700 mb-1">Members</div>
              <div className="bg-white border border-gray-300 rounded max-h-40 overflow-y-auto retro-scrollbar">
                {h.members.map(m => (
                  <div key={m.user_id} className="flex items-center justify-between px-2 py-1 border-b border-gray-100 text-xs" data-testid={`household-member-${m.username}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.status === "online" ? "#22c55e" : "#9ca3af" }} />
                      <span className="font-medium">{m.username}</span>
                      {m.user_id === h.owner_user_id && <Crown className="w-3 h-3 text-yellow-600" />}
                    </div>
                    {h.is_owner && m.user_id !== h.owner_user_id && (
                      <button onClick={() => removeMember(m.user_id, m.username)} disabled={busy} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Remove">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Owner-only: invite by username */}
            {h.is_owner && (
              <div className="bg-white border border-gray-300 p-2 rounded space-y-2">
                <div className="text-[11px] font-bold text-gray-700">Invite an existing user</div>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={inviteUsername}
                    onChange={e => setInviteUsername(e.target.value)}
                    placeholder="Username"
                    className="retro-input flex-1 text-xs"
                    data-testid="invite-username-input"
                  />
                  <button onClick={sendInvite} disabled={busy} className="retro-button px-2 py-1 text-xs flex items-center gap-1" data-testid="send-invite-btn">
                    <Send className="w-3 h-3" /> Invite
                  </button>
                </div>
                {h.pending_invitations?.length > 0 && (
                  <div className="text-[10px] text-gray-600">
                    Pending: {h.pending_invitations.map(p => p.username).join(", ")}
                  </div>
                )}
              </div>
            )}

            {/* Leave button (non-owners) */}
            {!h.is_owner && (
              <button onClick={leaveHousehold} disabled={busy} className="retro-button w-full text-xs py-1.5 flex items-center justify-center gap-1 text-red-700" data-testid="leave-household-btn">
                <LogOut className="w-3 h-3" /> Leave Household
              </button>
            )}
          </div>
        </div>
      )}

      {/* CASE 2: Pending invitations to other households */}
      {!h && data.pending_invites?.length > 0 && (
        <div className="retro-window" data-testid="pending-invites">
          <div className="bg-blue-700 text-white px-3 py-1.5 font-bold text-sm">📨 Pending Invitations</div>
          <div className="p-2 bg-[#E8E8E8] space-y-2">
            {data.pending_invites.map(inv => (
              <div key={inv.household_id} className="bg-white border border-gray-300 p-2 rounded">
                <div className="text-xs font-bold">{inv.household_name}</div>
                <div className="text-[10px] text-gray-600 mb-2">Invited by <b>{inv.owner_username}</b></div>
                <div className="flex gap-2">
                  <button onClick={() => acceptInvite(inv.household_id)} disabled={busy} className="retro-button text-xs px-2 py-1 flex items-center gap-1 bg-green-100" data-testid={`accept-invite-${inv.household_id}`}>
                    <Check className="w-3 h-3" /> Accept
                  </button>
                  <button onClick={() => declineInvite(inv.household_id)} disabled={busy} className="retro-button text-xs px-2 py-1 flex items-center gap-1" data-testid={`decline-invite-${inv.household_id}`}>
                    <X className="w-3 h-3" /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CASE 3: User has a pending request awaiting admin approval */}
      {!h && data.pending_request && (
        <div className="retro-window" data-testid="pending-request">
          <div className="bg-yellow-600 text-white px-3 py-1.5 font-bold text-sm">⏳ Request Pending</div>
          <div className="p-3 bg-[#FFF9D9] text-xs space-y-1">
            <div><b>{data.pending_request.name}</b></div>
            <div className="text-gray-700">Submitted {new Date(data.pending_request.created_at).toLocaleDateString()}</div>
            <div className="text-gray-700">An admin will review your request shortly. You'll be set as the household owner upon approval.</div>
          </div>
        </div>
      )}

      {/* CASE 4: No household, no request → show actions */}
      {!h && !data.pending_request && (
        <>
          {/* Join via invite code */}
          <div className="retro-window">
            <div className="bg-green-700 text-white px-3 py-1.5 font-bold text-sm">Join with Invite Code</div>
            <div className="p-3 bg-[#E8E8E8] space-y-2">
              <div className="text-[11px] text-gray-700">Got an invite code from a family member who already has a household? Enter it here.</div>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={joinCode}
                  onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="ABC123XY"
                  maxLength={10}
                  className="retro-input flex-1 text-xs uppercase tracking-widest font-mono"
                  data-testid="join-code-input"
                />
                <button onClick={joinByCode} disabled={busy} className="retro-button text-xs px-3 py-1 flex items-center gap-1" data-testid="join-code-btn">
                  <UserPlus className="w-3 h-3" /> Join
                </button>
              </div>
            </div>
          </div>

          {/* Request new household */}
          <div className="retro-window">
            <div className="bg-purple-700 text-white px-3 py-1.5 font-bold text-sm">Request a New Household</div>
            <div className="p-3 bg-[#E8E8E8] space-y-2">
              <div className="text-[11px] text-gray-700">No household yet? Request one — an admin will review and approve. You'll become the owner and get an invite code to share.</div>
              <input
                type="text"
                value={requestName}
                onChange={e => setRequestName(e.target.value)}
                placeholder="Household name (e.g. The Smith Family)"
                maxLength={60}
                className="retro-input w-full text-xs"
                data-testid="request-name-input"
              />
              <textarea
                value={requestReason}
                onChange={e => setRequestReason(e.target.value)}
                placeholder="Why (optional) — helps admins approve faster"
                maxLength={500}
                rows={2}
                className="retro-input w-full text-xs resize-none"
                data-testid="request-reason-input"
              />
              <button onClick={submitRequest} disabled={busy} className="retro-button w-full text-xs py-1.5 flex items-center justify-center gap-1" data-testid="submit-request-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Submit Request
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HouseholdSection;
